import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Briefcase, Mail, Trash2, UserRound } from 'lucide-react';
import { OutcomeReason, SubmissionStatus } from '@clawhire/shared';
import { useAuth } from '@/auth/AuthProvider';
import { formatPct, formatRate } from '@/lib/format';
import { PageHeader } from '@/components/ui/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { EnumBadge } from '@/components/data/EnumBadge';
import { ActivityTimeline } from '@/components/data/ActivityTimeline';
import { FollowUpList } from '@/components/data/FollowUpList';
import { FilesPanel } from '@/components/data/FilesPanel';
import {
  useDeleteSubmission,
  useRecalculateOffer,
  useSetSubmissionMargin,
  useSubmission,
  useUpdateSubmission,
} from '@/pages/submissions/api';
import { DraftEmailDialog } from '@/pages/submissions/DraftEmailDialog';
import { LinkButton } from '@/pages/submissions/LinkButton';
import { NotesBlock } from '@/pages/submissions/NotesBlock';
import { ProfilePanel } from '@/pages/submissions/ProfilePanel';
import { RateBlock } from '@/pages/submissions/RateBlock';
import { StatusStepper } from '@/pages/submissions/StatusStepper';
import { TrackingBlock } from '@/pages/submissions/TrackingBlock';
import { useSubmissionStatusChange } from '@/pages/submissions/useStatusChange';
import type { SubmissionPatch } from '@/pages/submissions/types';

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

/** `/submissions/:id` — status stepper, rates, tracking, notes, profile, and the side panels. */
export function SubmissionDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const query = useSubmission(id);
  const update = useUpdateSubmission(id);
  const setMargin = useSetSubmissionMargin(id);
  const recalculate = useRecalculateOffer(id);
  const status = useSubmissionStatusChange();
  const [emailOpen, setEmailOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const remove = useDeleteSubmission(id);

  if (query.isLoading) {
    return (
      <>
        <PageHeader breadcrumbs={[{ label: 'Submissions', to: '/submissions' }]} title="Submission" />
        <SkeletonLines lines={6} />
      </>
    );
  }
  if (query.isError || !query.data) {
    return (
      <>
        <PageHeader breadcrumbs={[{ label: 'Submissions', to: '/submissions' }]} title="Submission" />
        <EmptyState
          title="Submission not found"
          description={query.error ? query.error.message : `No submission matches "${id}".`}
          action={
            <LinkButton to="/submissions" variant="outline">
              Back to submissions
            </LinkButton>
          }
        />
      </>
    );
  }

  const s = query.data;
  const candidateName = s.candidate.fullName?.trim() || s.candidate.label?.trim() || '';
  const refetch = () => void query.refetch();
  const patch = (p: SubmissionPatch) => update.mutateAsync(p);
  const margin = async (marginPct: number | null) => {
    try {
      await setMargin.mutateAsync({ marginPct });
      toast.success(
        marginPct === null ? 'Margin override cleared' : `Margin override set to ${formatPct(marginPct)}`,
      );
    } catch (e) {
      toast.error(errorMessage(e, 'Could not change the margin'));
    }
  };
  const recalculateOffer = async () => {
    try {
      const detail = await recalculate.mutateAsync();
      toast.success(`Offer recalculated: ${formatRate(detail.clientRateEur)}`);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not recalculate the offer'));
    }
  };
  const confirmDelete = async () => {
    const title = s.computed.title;
    try {
      await remove.mutateAsync();
      setDeleteOpen(false);
      toast.success(`Submission ${title} deleted`);
      navigate('/submissions', { replace: true });
    } catch (e) {
      toast.error(errorMessage(e, 'Could not delete the submission'));
    }
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Submissions', to: '/submissions' }, { label: s.computed.title }]}
        title={s.computed.title}
        description={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link
              to={`/candidates/${s.candidate.id}`}
              className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
            >
              <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
              {s.candidate.candidateId}
              {candidateName ? ` · ${candidateName}` : ''}
            </Link>
            <Link
              to={`/positions/${s.position.id}`}
              className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
            >
              <Briefcase className="h-3.5 w-3.5" aria-hidden="true" />
              {s.position.requestNo} {s.position.role}
              {s.position.intermediary ? ` · ${s.position.intermediary.name}` : ''}
            </Link>
          </span>
        }
        meta={
          <>
            <EnumBadge def={SubmissionStatus} value={s.status} />
            {s.outcomeReason ? <EnumBadge def={OutcomeReason} value={s.outcomeReason} /> : null}
            {s.computed.needsChase ? <Badge variant="destructive">Needs chase</Badge> : null}
          </>
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setEmailOpen(true)}>
              <Mail aria-hidden="true" /> Draft e-mail
            </Button>
            {canWrite ? (
              <Button variant="outline" onClick={() => setDeleteOpen(true)}>
                <Trash2 aria-hidden="true" /> Delete
              </Button>
            ) : null}
          </>
        }
      />

      <StatusStepper
        status={s.status}
        disabled={!canWrite}
        busy={status.isPending}
        onSelect={(to) => status.request({ id: s.id, title: s.computed.title, from: s.status, to })}
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Rates</CardTitle>
              <CardDescription>
                Effective margin = this submission → position → global. The offer is suggested from the
                candidate rate and fixed once sent.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RateBlock
                submission={s}
                canEdit={canWrite}
                onPatch={patch}
                onMargin={margin}
                onRecalculate={recalculateOffer}
                recalculating={recalculate.isPending}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tracking</CardTitle>
              <CardDescription>Who we wait for and when to chase.</CardDescription>
            </CardHeader>
            <CardContent>
              <TrackingBlock submission={s} canEdit={canWrite} onPatch={patch} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
              <CardDescription>Fit summary and what to check in the interview.</CardDescription>
            </CardHeader>
            <CardContent>
              <NotesBlock value={s.notes} canEdit={canWrite} onSave={(notes) => patch({ notes })} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>The anonymised profile sent with this submission.</CardDescription>
            </CardHeader>
            <CardContent>
              <ProfilePanel submission={s} canEdit={canWrite} />
            </CardContent>
          </Card>
        </div>

        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <ActivityTimeline
                entityType="SUBMISSION"
                entityId={s.id}
                entries={s.activity}
                canAdd={canWrite}
                onChanged={refetch}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Follow-ups</CardTitle>
            </CardHeader>
            <CardContent>
              <FollowUpList
                entityType="SUBMISSION"
                entityId={s.id}
                items={s.followUps}
                canEdit={canWrite}
                onChanged={refetch}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Files</CardTitle>
            </CardHeader>
            <CardContent>
              <FilesPanel
                entityType="SUBMISSION"
                entityId={s.id}
                files={s.files}
                canEdit={canWrite}
                onChanged={refetch}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {status.dialog}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {s.computed.title}?</DialogTitle>
            <DialogDescription>
              The submission disappears from lists, the position and the candidate (soft delete). Its status,
              notes and rates are kept for the audit log only. The position stage and the candidate pool
              status are not changed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" loading={remove.isPending} onClick={() => void confirmDelete()}>
              Delete submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DraftEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        submissionId={s.id}
        context={s.computed.title}
      />
    </>
  );
}
